// This is the model to credit conversion rates, we should only add new model to credit calculations, never change existing model
// calculations because we store the credit conversion in aggregated rows, meaning if we change the calculation mid-cycle, we will invalidate aggregated rows

import { z } from "zod";

import { OrgRole } from "../enums.js";

import { AnthropicModel, DeprecatedAnthropicModel } from "./anthropicTypes.js";
import { BillableModel, RawBillingQuantityDetails } from "./billingTypes.js";
import { KimiModel } from "./chatCompletionsTypes.js";
import { DeprecatedOpenAiChatModel, OpenAiChatModel } from "./openAiTypes.js";

export const FREE_TIER_USER_CREDITS = 10;

export type AiCreditLimit = number | "UNLIMITED";

/**
 * Sentinel value indicating unlimited AI credits.

 * Note: This should not be used in comparisons, instead use isUnlimitedAiCredits
 */
export const UNLIMITED_AI_CREDITS_SENTINEL = "UNLIMITED";

/**
 * Returns whether the given credit limit is unlimited.
 */
export const isUnlimitedAiCreditLimit = (
  credits: AiCreditLimit,
): credits is "UNLIMITED" => credits === UNLIMITED_AI_CREDITS_SENTINEL;

/**
 * Type for org-level AI credit limit overrides by role.
 * When set on an org, these values override the default USER_CREDITS_BY_FEATURE_TIER_AND_ROLE
 * for all users in that org with the specified role.
 * - null or undefined for a role: no override, use default from USER_CREDITS_BY_FEATURE_TIER_AND_ROLE
 * - UNLIMITED_AI_CREDITS_SENTINEL: unlimited credits for that role
 * - 0: completely disable AI features for that role
 * - positive number: specific credit limit for that role
 */
export type OrgAiCreditLimitsByRoleOverride = Partial<
  Record<OrgRole, AiCreditLimit | null>
>;

/**
 * Zod schema for validating OrgAiCreditLimitsByRoleOverride at runtime.
 */
const aiCreditLimitSchema = z.union([
  z.number().int().min(0),
  z.literal("UNLIMITED"),
  z.null(),
]);

export const OrgAiCreditLimitsByRoleOverrideSchema = z.record(
  z.nativeEnum(OrgRole),
  aiCreditLimitSchema.optional(),
);

/** An integer of millionth of credits, 1_000_000 millionth of a credit = 1 credit */
type MillionthCredits = number;

export interface LongContextPricing {
  inputTokenThreshold: number;
  inputCostMultiplier: number;
  outputCostMultiplier: number;
}

const OPENAI_GPT_55_LONG_CONTEXT_PRICING: LongContextPricing = {
  inputTokenThreshold: 272_000,
  inputCostMultiplier: 2,
  outputCostMultiplier: 1.5,
};

const getLongContextPricingMultipliers = (
  usage: RawBillingQuantityDetails,
  longContextPricing?: LongContextPricing,
): {
  inputCostMultiplier: number;
  outputCostMultiplier: number;
} => {
  const totalInputTokens = usage.inputTokens + usage.cachedInputTokens;
  if (
    longContextPricing == null ||
    totalInputTokens <= longContextPricing.inputTokenThreshold
  ) {
    return { inputCostMultiplier: 1, outputCostMultiplier: 1 };
  }

  return {
    inputCostMultiplier: longContextPricing.inputCostMultiplier,
    outputCostMultiplier: longContextPricing.outputCostMultiplier,
  };
};

/** USD cents for one automated AI credit top-up invoice (~$25). */
export const AI_CREDIT_TOP_UP_AMOUNT_CENTS = 2500;

/** Credits in one standard auto top-up pack (user-facing). Keep aligned with `AI_CREDIT_TOP_UP_AMOUNT_CENTS` and `USER_CREDIT_VALUE_CENTS`. */
export const AI_CREDIT_TOP_UP_CREDIT_COUNT = 50;

/** Minimum workspace balance before a top-up is triggered */
export const AUTO_TOPUP_MINIMUM_BALANCE = 25;

/**
 * This is the total value we charge a user per-credit.
 * We need to track this here because we build a margin into the
 * cost per-million tokens and so need an anchor point to translate
 * tokens into credits.
 */
export const USER_CREDIT_VALUE_CENTS = 50;

export const FALLBACK_PRICING_MODEL: BillableModel =
  AnthropicModel.CLAUDE_4_6_SONNET;

export interface ModelPricingTable {
  costInCentsPerMilTokens: {
    inputTokens: number;
    outputTokens: number;
    cacheWrites: number;
    cacheReads: number;
  };
  marginPercent: number;
  longContextPricing?: LongContextPricing;
}

export const PRICING_BY_MODEL: Partial<
  Record<BillableModel, ModelPricingTable>
> = {
  [AnthropicModel.CLAUDE_4_6_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [AnthropicModel.CLAUDE_4_5_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_SONNET]: {
    costInCentsPerMilTokens: {
      inputTokens: 300,
      outputTokens: 1500,
      cacheWrites: 375,
      cacheReads: 30,
    },
    marginPercent: 0.4,
  },
  [AnthropicModel.CLAUDE_4_6_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [AnthropicModel.CLAUDE_4_7_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_NOUGAT_EAP]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_5_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 2500,
      cacheWrites: 625,
      cacheReads: 50,
    },
    marginPercent: 0.05,
  },
  [DeprecatedAnthropicModel.CLAUDE_4_OPUS]: {
    costInCentsPerMilTokens: {
      inputTokens: 1500,
      outputTokens: 7500,
      cacheWrites: 1875,
      cacheReads: 150,
    },
    marginPercent: 0.05,
  },
  [AnthropicModel.CLAUDE_4_5_HAIKU]: {
    costInCentsPerMilTokens: {
      inputTokens: 100,
      outputTokens: 500,
      cacheWrites: 125,
      cacheReads: 10,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_54]: {
    costInCentsPerMilTokens: {
      inputTokens: 250,
      outputTokens: 1500,
      cacheWrites: 0,
      cacheReads: 25,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_55]: {
    costInCentsPerMilTokens: {
      inputTokens: 500,
      outputTokens: 3000,
      cacheWrites: 0,
      cacheReads: 50,
    },
    marginPercent: 0.45,
    longContextPricing: OPENAI_GPT_55_LONG_CONTEXT_PRICING,
  },
  [OpenAiChatModel.CREST_ALPHA]: {
    costInCentsPerMilTokens: {
      inputTokens: 250,
      outputTokens: 1500,
      cacheWrites: 0,
      cacheReads: 25,
    },
    marginPercent: 0.45,
  },
  [DeprecatedOpenAiChatModel.GPT_52]: {
    costInCentsPerMilTokens: {
      inputTokens: 175,
      outputTokens: 1400,
      cacheWrites: 0,
      cacheReads: 17.5,
    },
    marginPercent: 0.45,
  },
  [OpenAiChatModel.GPT_41]: {
    costInCentsPerMilTokens: {
      inputTokens: 200,
      outputTokens: 800,
      cacheWrites: 0,
      cacheReads: 50,
    },
    marginPercent: 0.45,
  },
  [KimiModel.KIMI_K_2_6]: {
    costInCentsPerMilTokens: {
      inputTokens: 95,
      outputTokens: 400,
      cacheWrites: 0,
      cacheReads: 16,
    },
    marginPercent: 0.6,
  },
  [KimiModel.KIMI_K_2_6_THINKING]: {
    costInCentsPerMilTokens: {
      inputTokens: 95,
      outputTokens: 400,
      cacheWrites: 0,
      cacheReads: 16,
    },
    marginPercent: 0.6,
  },
};

const calculateMillionthCreditsPerTokenCoefficient = (
  costInCentsPerMilTokens: number,
  marginPercent: number,
): MillionthCredits => {
  const userCostPerMilTokens = costInCentsPerMilTokens / (1 - marginPercent);
  const creditsPerMilTokens = userCostPerMilTokens / USER_CREDIT_VALUE_CENTS;
  // Millionth-credit-per-token is the same as credits-per-million-tokens
  return creditsPerMilTokens;
};

export const getPricingTableForUsage = ({
  priceTable,
  usage,
}: {
  priceTable: ModelPricingTable;
  usage: RawBillingQuantityDetails;
}): ModelPricingTable => {
  const { inputCostMultiplier, outputCostMultiplier } =
    getLongContextPricingMultipliers(usage, priceTable.longContextPricing);
  if (inputCostMultiplier === 1 && outputCostMultiplier === 1) {
    return priceTable;
  }

  return {
    ...priceTable,
    costInCentsPerMilTokens: {
      ...priceTable.costInCentsPerMilTokens,
      inputTokens:
        priceTable.costInCentsPerMilTokens.inputTokens * inputCostMultiplier,
      cacheWrites:
        priceTable.costInCentsPerMilTokens.cacheWrites * inputCostMultiplier,
      cacheReads:
        priceTable.costInCentsPerMilTokens.cacheReads * inputCostMultiplier,
      outputTokens:
        priceTable.costInCentsPerMilTokens.outputTokens * outputCostMultiplier,
    },
  };
};

export const getCreditsFromUsageV2 = (
  usage: RawBillingQuantityDetails,
  model: BillableModel,
): MillionthCredits => {
  let priceTable: ModelPricingTable;
  if (PRICING_BY_MODEL[model] != null) {
    priceTable = PRICING_BY_MODEL[model];
  } else {
    priceTable = PRICING_BY_MODEL[FALLBACK_PRICING_MODEL]!;
  }
  priceTable = getPricingTableForUsage({ priceTable, usage });

  const inputTokenCost =
    usage.inputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.inputTokens,
      priceTable.marginPercent,
    );

  const outputTokenCost =
    usage.outputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.outputTokens,
      priceTable.marginPercent,
    );

  const cacheWriteCost =
    usage.cacheWrites *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.cacheWrites,
      priceTable.marginPercent,
    );

  const cacheReadsCost =
    usage.cachedInputTokens *
    calculateMillionthCreditsPerTokenCoefficient(
      priceTable.costInCentsPerMilTokens.cacheReads,
      priceTable.marginPercent,
    );

  // We don't care about billing for fractional ten-millionth credits.
  // Additionally, from this point it's easier for us if this number is an integer.
  return Math.floor(
    inputTokenCost + outputTokenCost + cacheWriteCost + cacheReadsCost,
  );
};
